import { type RefObject, useEffect } from 'react';

/**
 * Custom hook to handle video src updates and clearing the video element when src is undefined.
 *
 * @param videoRef - A reference to the video element.
 * @param videoSrc - The source URL for the video.
 *
 * This hook will update the `src` attribute of the video element when the `videoSrc` changes.
 * If the `videoSrc` is undefined or null, it will clear the video element, stop playback,
 * and ensure the old video content is removed from view.
 */
export const useUpdateVideoSrc = (
  videoRef: RefObject<HTMLVideoElement>,
  videoSrc?: string
) => {
  useEffect(() => {
    const videoElement = videoRef.current;

    if (videoElement && videoSrc) {
      // If the videoSrc is valid, set the src attribute of the video element
      videoElement.src = videoSrc;
    } else if (videoElement) {
      // If the videoSrc is undefined or null, clear the video element
      videoElement.src = '';
      videoElement.load(); // This reloads the video element and clears the preview
    }
  }, [videoSrc, videoRef]);
};
